import { Tokenizer, type TokenizerObject, type Tokens } from 'marked';

import {
  EMPTY_SECTION_HEADING_MARKER_REGEX,
  SECTION_END_MARKER_REGEX,
} from './sections/section-markers';

/**
 * Tags the markdown-native schema renders, or unwraps without losing content.
 *
 * `@tiptap/markdown` hands every marked `html` token to `generateJSON`, which
 * DOM-parses it against the editor schema. A known element becomes a node or
 * mark; a structural wrapper unwraps to its children. Both keep the user's
 * words. Everything absent from this set is *deleted* by that parse — element
 * and text alike — so it has to be literalized to plain text instead.
 *
 * Membership is the policy knob, covering what actually reaches stored note
 * markdown: `<ul><li>` blocks from note generation (see the
 * `html-block-adjacent-section` fixture), the vMarkdown serializer's in-cell
 * list html, and the standard tags chat inserts pass through deliberately
 * (`stripCustomChatTags`).
 *
 * `h1` is excluded on purpose: it parses to a level-1 heading, the section
 * delimiter, so keeping it would let typed markup silently split a note.
 */
const RENDERABLE_TAGS = new Set(
  // Inline formatting (`a` is conditional — see `isRenderableInEditor`).
  (
    'a b strong i em s del strike u sub sup code br ' +
    // Blocks the schema has nodes for, plus the table family.
    'blockquote h2 h3 h4 h5 h6 hr li ol pre ul ' +
    'caption col colgroup table tbody td tfoot th thead tr ' +
    // Structural wrappers, which unwrap to their children.
    'div p section span'
  ).split(' ')
);

/**
 * Reserved comments the vMarkdown editor writes but markdown-native never does.
 * They must stay html so they keep being consumed rather than surfacing as text
 * on a clinical surface. The two grammars mirror the reserved-comment source in
 * `src/helpers/markdownToHtml.ts`; the section markers are imported rather than
 * restated.
 */
const LEGACY_RESERVED_COMMENT_REGEX =
  /^<!--\s*(?:ehr-link:[\s\S]*?|colwidths:[^\n]*?)-->$/;

const isReservedSystemComment = (trimmed: string): boolean =>
  SECTION_END_MARKER_REGEX.test(trimmed) ||
  EMPTY_SECTION_HEADING_MARKER_REGEX.test(trimmed) ||
  LEGACY_RESERVED_COMMENT_REGEX.test(trimmed);

// Reads the element name out of a token marked has *already* lexed as html —
// not a grammar decision, so no rule is re-implemented here.
const TAG_NAME = /^<\/?\s*([a-zA-Z][a-zA-Z0-9-]*)/;
const IS_CLOSING_TAG = /^<\s*\//;
const HAS_HREF = /\shref\s*=/i;

const htmlTagName = (trimmed: string): string | undefined =>
  TAG_NAME.exec(trimmed)?.[1]?.toLowerCase();

/**
 * True when a marked html token should stay html. False means it must be
 * literalized — handed back to marked's text rules so the characters survive as
 * the user typed them, instead of being deleted by the schema parse.
 *
 * `<a>` is the one attribute-conditional case. The schema's Link mark matches
 * `a[href]` only, so a bare `<a>` is dropped and its brackets with it.
 * `insideKeptAnchor` answers the same question for the closing tag, which
 * carries no attributes of its own: strand a `</a>` whose opener was
 * literalized and `generateJSON` injects a stray empty paragraph mid-sentence
 * and eats the text.
 */
export const isRenderableInEditor = (raw: string, insideKeptAnchor = false): boolean => {
  const trimmed = raw.trim();
  if (trimmed.startsWith('<!--')) return isReservedSystemComment(trimmed);

  const tagName = htmlTagName(trimmed);
  if (tagName === undefined) return false;
  if (tagName === 'a') {
    return IS_CLOSING_TAG.test(trimmed) ? insideKeptAnchor : HAS_HREF.test(trimmed);
  }
  return RENDERABLE_TAGS.has(tagName);
};

/**
 * The html half of the markdown-native tokenizer: marked keeps classifying html
 * exactly as CommonMark says, and this decides only what happens *after* that
 * classification — render it, or hand the characters back as text.
 *
 * `blockHtml` is injected rather than reached for directly so the section-
 * boundary cut stays composed into the same override. Two `use()` calls could
 * not do this: marked chains a tokenizer override to the previous one only when
 * it returns `false`, and literalization returns `undefined` (no token at all).
 */
export const createHtmlLiteralizationTokenizer = (
  blockHtml: (this: Tokenizer, src: string) => Tokens.HTML | undefined
): TokenizerObject => ({
  html(src: string) {
    const token = blockHtml.call(this, src);
    return !token || isRenderableInEditor(token.raw) ? token : undefined;
  },

  tag(src: string) {
    // marked flips `inLink`/`inRawBlock` inside the tokenizer, before we get to
    // veto the token, so both are captured first.
    const { state } = this.lexer;
    const wasInLink = state.inLink;
    const wasInRawBlock = state.inRawBlock;

    const token = Tokenizer.prototype.tag.call(this, src);
    if (!token) return token;

    const trimmed = token.raw.trim();
    const renderable = isRenderableInEditor(token.raw, wasInLink);

    // `inLink` carries the anchor pairing, so it has to mean "a KEPT anchor is
    // open" — decided by the same href test the opener used. marked's own value
    // cannot be trusted for this: it recognizes an opener only as the literal
    // `<a `, so `<a\nhref="…">` would leave the flag unset and strand the
    // matching `</a>`, dropping the link and leaking the closer as text.
    state.inLink =
      htmlTagName(trimmed) === 'a'
        ? renderable && !IS_CLOSING_TAG.test(trimmed)
        : wasInLink;

    if (renderable) return token;
    state.inRawBlock = wasInRawBlock;
    return undefined;
  },
});
